import React from 'react';
import SegmentedControl from '../SegmentedControl';

export interface StatusSegment<T extends string> {
  value: T;
  label: string;
  /** Rendered as a small count badge after the label. */
  count?: number;
  disabled?: boolean;
}

interface StatusSegmentsProps<T extends string> {
  segments: StatusSegment<T>[];
  value: T;
  onChange: (value: T) => void;
  'aria-label': string;
  size?: 'sm' | 'md';
  /** Stretch across the row (phones). */
  fullWidth?: boolean;
}

/**
 * The register's status facet -- All / Drafts / Signed ... -- as a segmented
 * control with count badges. One value is always selected; there is no empty
 * state, so `onChange` never receives null.
 */
function StatusSegments<T extends string>({
  segments,
  value,
  onChange,
  'aria-label': ariaLabel,
  size = 'md',
  fullWidth = false,
}: StatusSegmentsProps<T>): React.ReactElement {
  return (
    <SegmentedControl<T>
      aria-label={ariaLabel}
      size={size}
      fullWidth={fullWidth}
      value={value}
      onChange={(v) => {
        if (v !== null) onChange(v);
      }}
      options={segments.map((s) => ({
        value: s.value,
        disabled: s.disabled,
        label: (
          <>
            {s.label}
            {s.count !== undefined && <span className="seg-count">{s.count.toLocaleString()}</span>}
          </>
        ),
      }))}
    />
  );
}

export default StatusSegments;
